import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fmtAUD, fmtDate, fmtDateTime, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Pill, STAGE_STYLES } from "@/components/badges";
import TransferModal from "@/components/TransferModal";
import { ChevronLeft, History, FileText, MessageSquare, CheckSquare, ArrowLeftRight, Trash2, AlertTriangle, Activity as ActivityIcon } from "lucide-react";
import { toast } from "sonner";
import ActivityTab from "@/components/ActivityTab";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";

const STAGES = ["Need Assessment", "Formal Presentation", "Decision Pending", "Closed Won", "Closed Lost"];
const TABS = [
  { id: "overview", label: "Overview", icon: FileText },
  { id: "activity", label: "Activity", icon: ActivityIcon },
  { id: "notes", label: "Notes", icon: MessageSquare },
  { id: "audit", label: "Audit Trail", icon: History },
  { id: "tasks", label: "Tasks", icon: CheckSquare },
];

export default function OpportunityDetail() {
  const { id } = useParams();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const isAdmin = currentUser?.role === "admin";
  const [opp, setOpp] = useState(null);
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [tab, setTab] = useState("overview");
  const [notes, setNotes] = useState([]);
  const [audit, setAudit] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [showTransfer, setShowTransfer] = useState(false);
  const [showDeleteConfirm1, setShowDeleteConfirm1] = useState(false);
  const [showDeleteConfirm2, setShowDeleteConfirm2] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [pending, setPending] = useState({});
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    const [o, u, s] = await Promise.all([api.get(`/opportunities/${id}`), api.get("/users"), api.get("/service-categories")]);
    setOpp(o.data); setUsers(u.data); setServices(s.data);
  };

  useEffect(() => { reload().catch(() => toast.error("Opportunity not found")); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => {
    if (tab === "notes") api.get(`/notes?entity_type=opportunity&entity_id=${id}`).then((r) => setNotes(r.data));
    if (tab === "audit") api.get(`/audit?entity_type=opportunity&entity_id=${id}`).then((r) => setAudit(r.data));
    if (tab === "tasks") api.get(`/tasks`).then((r) => setTasks(r.data.filter((t) => t.related_id === id)));
  }, [tab, id]);

  if (!opp) return <div className="p-8 text-slate-400">Loading…</div>;
  const usersById = Object.fromEntries(users.map((u) => [u.id, u]));
  const dirty = Object.keys(pending).length > 0;
  const view = { ...opp, ...pending };

  const save = (patch) => {
    setPending((cur) => {
      const next = { ...cur, ...patch };
      for (const k of Object.keys(patch)) {
        if (JSON.stringify(next[k]) === JSON.stringify(opp[k])) delete next[k];
      }
      return next;
    });
  };

  const saveAll = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/opportunities/${id}`, pending);
      setOpp(data);
      setPending({});
      toast.success("Changes saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const discardAll = () => {
    if (!dirty) return;
    setPending({});
    toast.message("Changes discarded");
  };

  const handleDelete = async () => {
    if (deleteText !== "DELETE") {
      toast.error('Type "DELETE" to confirm');
      return;
    }
    setDeleting(true);
    try {
      await api.delete(`/opportunities/${id}`, { headers: { "X-Confirm-Delete": "DELETE" } });
      toast.success("Opportunity deleted");
      navigate("/opportunities");
    } catch (e) {
      toast.error(formatApiError(e));
      setDeleting(false);
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    await api.post(`/notes?entity_type=opportunity&entity_id=${id}`, { content: noteText, stage: opp.stage });
    setNoteText("");
    const r = await api.get(`/notes?entity_type=opportunity&entity_id=${id}`); setNotes(r.data);
    toast.success("Note added");
  };

  const weighted = (Number(opp.estimated_value) || 0) * (Number(opp.probability) || 0) / 100;
  const isWon = opp.stage === "Closed Won";

  return (
    <div className="p-8" data-testid="opportunity-detail-page">
      <Link to="/opportunities" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4"><ChevronLeft className="w-4 h-4" /> Back to opportunities</Link>

      {dirty && (
        <div className="sticky top-0 z-20 -mx-8 mb-6 px-8 py-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between" data-testid="opp-save-bar">
          <div className="text-sm text-amber-900">
            <span className="font-semibold">{Object.keys(pending).length}</span> unsaved change{Object.keys(pending).length === 1 ? "" : "s"}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" data-testid="opp-discard-btn" onClick={discardAll} disabled={saving} className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-md">Discard</button>
            <button type="button" data-testid="opp-save-btn" onClick={saveAll} disabled={saving} className="px-4 py-1.5 text-xs font-semibold bg-[#1B4F8B] hover:bg-[#143b67] disabled:opacity-50 text-white rounded-md">
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      )}

      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="eyb-label">Opportunity</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">{opp.name}</h1>
          <div className="flex items-center gap-2 mt-3">
            <Pill className={STAGE_STYLES[opp.stage]}>{opp.stage}</Pill>
            <span className="text-xs text-slate-500">{opp.business_name}</span>
          </div>
        </div>
        <div className="flex items-start gap-4">
          {isAdmin && (
            <button
              data-testid="delete-opp-btn"
              onClick={() => setShowDeleteConfirm1(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-rose-200 text-rose-600 text-xs font-semibold hover:bg-rose-50 transition-colors"
              title="Delete this opportunity (admin only)"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          )}
          <div className="text-right">
            <div className="eyb-label">Estimated Value</div>
            <div className="font-heading text-3xl font-bold text-slate-900">{fmtAUD(opp.estimated_value)}</div>
            {isWon && <div className="text-xs font-semibold text-emerald-700 mt-1">Won at 100%</div>}
          </div>
        </div>
      </div>

      <div className="flex border-b border-slate-200 mb-6">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)} className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px inline-flex items-center gap-2 ${tab === t.id ? "border-[#29ABE2] text-[#187199]" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="eyb-card p-6 lg:col-span-2 space-y-4">
            <h3 className="font-heading font-semibold text-slate-900">Deal Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <Edit label="Stage" select value={view.stage} options={STAGES} onSave={(v) => save({ stage: v })} />
              <Edit label="Estimated Value (AUD)" type="number" value={view.estimated_value} onSave={(v) => save({ estimated_value: Number(v) })} />
              <Edit label="Expected Close Date" type="date" value={view.expected_close_date} onSave={(v) => save({ expected_close_date: v })} />
            </div>
            <div>
              <div className="eyb-label mb-2">Service Categories</div>
              <div className="flex flex-wrap gap-2">
                {services.filter((s) => s.active).map((s) => {
                  const active = (opp.service_categories || []).includes(s.name);
                  return (
                    <button key={s.id} onClick={() => save({ service_categories: active ? opp.service_categories.filter((x) => x !== s.name) : [...(opp.service_categories || []), s.name] })}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border ${active ? "bg-[#29ABE2] text-white border-[#29ABE2]" : "bg-white text-slate-600 border-slate-200 hover:border-[#29ABE2]"}`}>
                      {s.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <h3 className="font-heading font-semibold text-slate-900 mt-6 mb-3">Stage History</h3>
              <ol className="relative border-l-2 border-slate-100 ml-3 space-y-4">
                {(opp.stage_history || []).map((h, i) => (
                  <li key={i} className="pl-6 relative">
                    <span className="absolute -left-2 top-1 w-3 h-3 bg-[#29ABE2] rounded-full ring-4 ring-white" />
                    <div className="text-sm font-semibold text-slate-800">{h.stage}</div>
                    <div className="text-[11px] text-slate-500">{fmtDateTime(h.entered_at)} · {usersById[h.user_id]?.name || "—"}</div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
          <div className="eyb-card p-6 space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-semibold text-slate-900">Ownership</h3>
              <button
                data-testid="transfer-opp-btn"
                onClick={() => setShowTransfer(true)}
                className="text-xs font-semibold text-[#1f8ebf] hover:text-[#187199] inline-flex items-center gap-1"
              >
                <ArrowLeftRight className="w-3.5 h-3.5" /> Transfer
              </button>
            </div>
            <Info label="Owner" value={usersById[opp.assigned_to]?.name || "—"} />
            <Info label="Created" value={fmtDateTime(opp.created_at)} />
            <Info label="Updated" value={fmtDateTime(opp.updated_at)} />
            <Info label="Source" value={opp.source || "—"} />
            <Info label="Contact" value={opp.contact_person || "—"} />
            <Info label="Email" value={opp.email || "—"} />
            <Info label="Phone" value={opp.phone || "—"} />
            {opp.lead_id && <Link to={`/leads/${opp.lead_id}`} className="text-xs text-[#1f8ebf] hover:underline mt-2 block">View original lead →</Link>}
          </div>
        </div>
      )}

      {tab === "overview" && (opp.ownership_history || []).length > 0 && (
        <div className="eyb-card p-6 mt-6" data-testid="opp-ownership-history">
          <div className="flex items-center gap-2 mb-4">
            <ArrowLeftRight className="w-4 h-4 text-slate-400" />
            <h3 className="font-heading font-semibold text-slate-900">Ownership History</h3>
            <span className="text-xs text-slate-400">{opp.ownership_history.length} transfer{opp.ownership_history.length === 1 ? "" : "s"}</span>
          </div>
          <ol className="relative border-l-2 border-slate-100 ml-3 space-y-4">
            {[...opp.ownership_history].reverse().map((h, i) => (
              <li key={i} className="pl-6 relative">
                <span className="absolute -left-2 top-1.5 w-3 h-3 bg-[#29ABE2] rounded-full ring-4 ring-white" />
                <div className="text-sm text-slate-800">
                  <span className="font-semibold">{h.from_user_name}</span>
                  <span className="text-slate-500 mx-1.5">→</span>
                  <span className="font-semibold">{h.to_user_name}</span>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Transferred by {h.by_user_name} · {fmtDateTime(h.at)}
                  {h.notified && <span className="ml-2 text-emerald-600">· Notified by email</span>}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {tab === "activity" && <ActivityTab entityType="opportunity" entityId={id} />}

      {tab === "notes" && (
        <div className="eyb-card p-6">
          <div className="flex gap-2 mb-6">
            <input value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a note…" className="flex-1 px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#29ABE2]/40" />
            <button onClick={addNote} className="px-4 py-2 bg-[#29ABE2] hover:bg-[#1f8ebf] text-white text-sm font-semibold rounded-full">Add</button>
          </div>
          <ol className="relative border-l-2 border-slate-100 ml-3 space-y-6">
            {notes.length === 0 && <li className="text-sm text-slate-400 pl-4">No notes yet.</li>}
            {notes.map((n) => (
              <li key={n.id} className="pl-6 relative">
                <span className="absolute -left-2 top-1 w-3 h-3 bg-[#29ABE2] rounded-full ring-4 ring-white" />
                <div className="text-sm text-slate-800 whitespace-pre-wrap">{n.content}</div>
                <div className="text-[11px] text-slate-500 mt-1">{n.created_by_name} · {fmtDateTime(n.created_at)} · Stage: {n.stage || "—"}</div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {tab === "audit" && (
        <div className="eyb-card p-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <th className="py-2">When</th><th className="py-2">User</th><th className="py-2">Action</th><th className="py-2">Changes</th>
              </tr>
            </thead>
            <tbody>
              {audit.map((a) => (
                <tr key={a.id} className="border-b border-slate-50">
                  <td className="py-2 text-xs text-slate-500">{fmtDateTime(a.timestamp)}</td>
                  <td className="py-2">{a.user_name}</td>
                  <td className="py-2 capitalize">{a.action}</td>
                  <td className="py-2 text-xs">
                    {(a.changes || []).map((c, i) => (
                      <div key={i}><span className="font-semibold text-slate-600">{c.field}:</span> <span className="text-rose-500">{c.old_value || "∅"}</span> → <span className="text-emerald-600">{c.new_value}</span></div>
                    ))}
                  </td>
                </tr>
              ))}
              {audit.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-slate-400 text-xs">No audit history.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "tasks" && <OppTasks tasks={tasks} users={users} relatedId={id} onChange={async () => { const r = await api.get("/tasks"); setTasks(r.data.filter((t) => t.related_id === id)); }} />}

      {showTransfer && <TransferModal entityType="opportunity" entityId={id} currentOwnerId={opp.assigned_to} users={users} onClose={() => setShowTransfer(false)} onDone={() => { setShowTransfer(false); reload(); }} />}

      {/* Step 1 — soft warn */}
      <AlertDialog open={showDeleteConfirm1} onOpenChange={(o) => !o && setShowDeleteConfirm1(false)}>
        <AlertDialogContent data-testid="delete-opp-confirm-1">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Delete this opportunity?
            </AlertDialogTitle>
            <AlertDialogDescription>
              You are about to delete <span className="font-semibold text-slate-900">{opp.name}</span>
              {opp.business_name ? <> ({opp.business_name})</> : null}. This will remove the deal from your pipeline and revenue forecasts. Notes and audit history will remain in the audit log but the opportunity record will be gone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-opp-cancel-1">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-opp-continue-1"
              className="bg-rose-600 hover:bg-rose-700"
              onClick={() => { setDeleteText(""); setShowDeleteConfirm1(false); setTimeout(() => setShowDeleteConfirm2(true), 180); }}
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Step 2 — typed confirm */}
      <Dialog open={showDeleteConfirm2} onOpenChange={(o) => { if (!o && !deleting) { setShowDeleteConfirm2(false); setDeleteText(""); } }}>
        <DialogContent data-testid="delete-opp-confirm-2" className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Trash2 className="w-5 h-5" /> Final confirmation
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. Type <span className="font-mono font-bold text-slate-900">DELETE</span> below to permanently remove this opportunity.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Input
              data-testid="delete-opp-typed-input"
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value)}
              placeholder='Type "DELETE" here'
              autoFocus
            />
          </div>
          <DialogFooter>
            <button
              type="button"
              data-testid="delete-opp-cancel-2"
              disabled={deleting}
              onClick={() => { setShowDeleteConfirm2(false); setDeleteText(""); }}
              className="px-4 py-2 rounded-md border border-slate-200 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="delete-opp-final-btn"
              disabled={deleteText !== "DELETE" || deleting}
              onClick={handleDelete}
              className="px-4 py-2 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {deleting ? "Deleting…" : "Delete opportunity"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OppTasks({ tasks, users, relatedId, onChange }) {
  const [form, setForm] = useState({ title: "", due_date: "", priority: "Medium", assigned_to: "" });
  const create = async () => {
    if (!form.title) return;
    await api.post("/tasks", { ...form, related_type: "opportunity", related_id: relatedId });
    setForm({ title: "", due_date: "", priority: "Medium", assigned_to: "" });
    onChange();
  };
  return (
    <div className="eyb-card p-6">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-6">
        <input placeholder="Task title…" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="md:col-span-2 px-3 py-2 border border-slate-200 rounded-md text-sm" />
        <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="px-3 py-2 border border-slate-200 rounded-md text-sm" />
        <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
          {["Low", "Medium", "High"].map((p) => <option key={p}>{p}</option>)}
        </select>
        <button onClick={create} className="px-4 py-2 bg-[#29ABE2] hover:bg-[#1f8ebf] text-white text-sm font-semibold rounded-full">Add</button>
      </div>
      <ul className="divide-y divide-slate-100">
        {tasks.map((t) => (
          <li key={t.id} className="py-3 flex items-center justify-between">
            <div>
              <div className="font-medium text-slate-800">{t.title}</div>
              <div className="text-xs text-slate-500">Due {t.due_date || "—"} · {t.priority}</div>
            </div>
            <select value={t.status} onChange={async (e) => { await api.patch(`/tasks/${t.id}`, { status: e.target.value }); onChange(); }} className="px-2 py-1 border border-slate-200 rounded-md text-xs bg-white">
              {["Open", "In Progress", "Completed"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </li>
        ))}
        {tasks.length === 0 && <li className="text-sm text-slate-400 py-2">No tasks yet.</li>}
      </ul>
    </div>
  );
}

function Info({ label, value }) {
  return <div className="flex items-center justify-between text-sm py-1.5 border-b border-slate-50"><span className="eyb-label">{label}</span><span className="text-slate-700 truncate ml-2">{value}</span></div>;
}

function Edit({ label, value, options, select, type = "text", onSave }) {
  const [v, setV] = useState(value ?? "");
  useEffect(() => setV(value ?? ""), [value]);
  if (select) {
    return (
      <label className="block">
        <span className="eyb-label">{label}</span>
        <select value={v} onChange={(e) => { setV(e.target.value); onSave(e.target.value); }} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
          {options.map((o) => typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </label>
    );
  }
  return (
    <label className="block">
      <span className="eyb-label">{label}</span>
      <input type={type} value={v} onChange={(e) => setV(e.target.value)} onBlur={() => v !== (value ?? "") && onSave(v)} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
    </label>
  );
}
